import { useState } from 'react';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { FileDropZone, type DropFileRow } from './FileDropZone';
import { SourceDocLibrary } from './SourceDocLibrary';
import { bbFilesService } from '@/services/bbFilesService';
import { bbFilesKeys, useBbFiles } from '@/hooks/useBbFiles';

function errorReason(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return fallback;
}

export function DropReceiptBanner({
  remaining,
  byLetter,
  onReview,
}: {
  remaining: number;
  byLetter: { letter: string; count: number }[];
  onReview: () => void;
}) {
  if (remaining <= 0) return null;
  const letterBits = byLetter.map((s) => `${s.letter} ${s.count}`).join(' · ');
  return (
    <div
      data-testid="bb-drop-receipt"
      className="mt-3 flex flex-wrap items-center gap-2.5 rounded-lg border border-coral/20 bg-coral-light px-3 py-2.5"
    >
      <div className="min-w-0 flex-1 text-[12.5px] text-coral-dark">
        {remaining} draft{remaining === 1 ? '' : 's'} from this drop still need a decision
        {letterBits ? ` · ${letterBits}` : ''}
      </div>
      <span className="flex-none rounded-[5px] bg-card px-1.5 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-coral-dark">
        {remaining} left
      </span>
      <button
        type="button"
        onClick={onReview}
        className="flex-none text-[12.5px] font-semibold text-coral hover:text-coral-hover"
      >
        Review drafts →
      </button>
    </div>
  );
}

export function BbFileDropCard({
  tenantId,
  onJumpToDrafts,
  onReviewDrafts,
  onFilesStored,
  dropRemaining = 0,
  dropByLetter = [],
}: {
  tenantId: string;
  /** Optional: jump into the attention queue filtered to a file's drafts. */
  onJumpToDrafts?: (fileId: string) => void;
  /** Starts the drop-filtered Verify-next walk for this session's files. */
  onReviewDrafts?: () => void;
  /** Every newly stored file id this session — parent owns the drop batch. */
  onFilesStored?: (fileIds: string[]) => void;
  dropRemaining?: number;
  dropByLetter?: { letter: string; count: number }[];
}) {
  const [rows, setRows] = useState<DropFileRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  // Stored originals + the reader flag (contract B.2 — the flag rides on the
  // list response, so nothing has to probe /read to discover it).
  const { data: filesView, isPending, isError, refetch } = useBbFiles(tenantId);
  const docs = filesView?.docs ?? [];
  const readerEnabled = filesView?.readerEnabled;

  const refreshFiles = () =>
    void queryClient.invalidateQueries({ queryKey: bbFilesKeys.list(tenantId) });

  /**
   * Preview: stream the stored original and hand it to the browser. The object
   * URL is revoked on the next tick — the new tab has already claimed the blob
   * by then, and holding it would leak for the life of the page.
   */
  const handlePreview = async (fileId: string) => {
    try {
      const blob = await bbFilesService.fetchFileContent(tenantId, fileId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      // Preview is non-destructive and non-critical: a failure must not take
      // down the card that also owns uploading.
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await bbFilesService.deleteFile(tenantId, fileId);
    } finally {
      refreshFiles();
    }
  };

  const patchRow = (id: string, patch: Partial<DropFileRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleFiles = async (accepted: File[], rejectedNames: string[]) => {
    const rejectedRows: DropFileRow[] = rejectedNames.map((name) => ({
      id: crypto.randomUUID(),
      name,
      state: 'rejected',
      detail: 'Unsupported file type — accepted: .docx .pdf .txt .md',
    }));
    const tooManyFiles = accepted.length > 10;
    const acceptedRows: DropFileRow[] = accepted.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      state: tooManyFiles ? 'rejected' : 'uploading',
      detail: tooManyFiles ? 'Nothing uploaded — select at most 10 files at a time.' : undefined,
    }));
    if (rejectedRows.length || acceptedRows.length) {
      setRows((prev) => [...prev, ...rejectedRows, ...acceptedRows]);
    }
    if (accepted.length === 0 || tooManyFiles) return;

    setUploading(true);
    try {
      const res = await bbFilesService.uploadFiles(tenantId, accepted);
      for (const [index, row] of acceptedRows.entries()) {
        const result = res.files[index];
        const id = row.id;
        if (!result) {
          patchRow(id, { state: 'unconfirmed', detail: 'No upload result returned. Check the document list before retrying.' });
        } else if ((result.status === 'stored' || result.status === 'restored') && result.file_id) {
          patchRow(id, { state: 'stored', fileId: result.file_id });
          onFilesStored?.([result.file_id]);
        } else if (result.status === 'duplicate') {
          patchRow(id, { state: 'duplicate', fileId: result.file_id ?? undefined });
        } else if (result.status === 'rejected') {
          patchRow(id, { state: 'rejected', detail: result.reason ?? 'Rejected' });
        } else {
          patchRow(id, { state: 'unconfirmed', detail: 'Upload not confirmed. Check the document list before retrying.' });
        }
      }
    } catch (err) {
      const reason = errorReason(err, 'Upload not confirmed. Check the document list before retrying.');
      acceptedRows.forEach((r) => patchRow(r.id, { state: 'unconfirmed', detail: reason }));
    } finally {
      setUploading(false);
      refreshFiles();
    }
  };

  return (
    <div id="bb-import-card" className="rounded-2xl border border-border bg-card shadow-sm mb-6 p-4">
      <div className="mb-3">
        <div className="text-[13.5px] font-semibold">Upload brand documents</div>
        <div className="text-[11.5px] text-muted-foreground mt-0.5">
          Store brand guides, price sheets, or policy docs for {tenantId}. Uploading does not
          extract facts, create drafts, or change the score.
        </div>
      </div>
      <FileDropZone
        files={rows}
        disabled={uploading}
        onFiles={(a, r) => void handleFiles(a, r)}
        onReviewFile={onJumpToDrafts}
      />
      {onReviewDrafts && (
        <DropReceiptBanner remaining={dropRemaining} byLetter={dropByLetter} onReview={onReviewDrafts} />
      )}

      <div className="mt-4">
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-[12.5px] font-semibold">Source documents</span>
          {readerEnabled === false && (
            <span className="text-[11px] text-muted-foreground">
              reader not enabled here — files are stored, not read
            </span>
          )}
        </div>
        {isPending ? (
          <p role="status" className="text-sm text-muted-foreground">Loading documents…</p>
        ) : isError ? (
          <div role="alert" className="text-sm text-destructive">
            Could not load stored documents.{' '}
            <button type="button" onClick={() => void refetch()} className="underline">Retry</button>
          </div>
        ) : (
          <SourceDocLibrary
            docs={docs}
            onOpen={(id) => void handlePreview(id)}
            onJumpToDrafts={onJumpToDrafts}
            onDelete={(id) => void handleDelete(id)}
          />
        )}
      </div>
    </div>
  );
}
